/*
  ves-help-center.js

  Wires the four interactive surfaces of the Help Center page:

  1. Search filter, listens for `input` on the hero's search input
     (`[data-ves-help-search-input]`). When typing, filters every
     `.ves-help-faq-item` across every category by textContent match,
     opens matching items, hides non-matching ones, hides "home" blocks
     (featured guide / most-asked / category grid + grid label), shows
     "<n> results for <query>" line, and a no-results state when zero.
     Empty query restores the home view.

  2. Category tabs, clicking `.ves-help-cat-card` swaps `.is-active`
     on the cards and shows the corresponding
     `[data-ves-help-cat-section]` while hiding the others.

  3. Hash routing, `#<key>` on load (and on hashchange) selects that
     category and scrolls to the cards.

  4. YouTube lightbox, any `[data-ves-help-video]` button opens a
     full-screen <dialog> with the embedded video; Escape and overlay
     click close.

  The script is idempotent (re-bound safely after section editor
  re-renders) and quietly no-ops if the help-center sections aren't on
  the page.
*/

(function () {
  'use strict';

  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function $(sel, root) { return (root || document).querySelector(sel); }

  // ------------------------------------------------------------------
  // 1. SEARCH FILTER + 2. CATEGORY TABS + 3. HASH ROUTING
  // ------------------------------------------------------------------

  function initHelpCenter() {
    var root = $('[data-ves-help-categories-root]');
    var searchInput = $('[data-ves-help-search-input]');
    var clearBtn = $('[data-ves-help-search-clear]');
    if (!root) return;

    var cards = $$('.ves-help-cat-card', root);
    var sections = $$('[data-ves-help-cat-section]', root);
    var faqItems = $$('.ves-help-faq-item', root);
    var homeBlocks = $$('[data-ves-help-home-block]');
    var searchInfo = $('[data-ves-help-search-info]', root);
    var noResults = $('[data-ves-help-no-results]', root);

    var validKeys = cards.map(function (c) { return c.dataset.vesHelpCat; });
    var activeCat = validKeys[0] || null;
    var currentView = 'home';

    function setHomeBlocks(visible) {
      homeBlocks.forEach(function (el) {
        if (visible) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    }

    function setActiveCategory(catKey) {
      if (validKeys.indexOf(catKey) === -1) catKey = validKeys[0];
      if (!catKey) return;
      activeCat = catKey;

      cards.forEach(function (card) {
        var match = card.dataset.vesHelpCat === catKey;
        card.classList.toggle('is-active', match);
        card.setAttribute('aria-selected', match ? 'true' : 'false');
      });

      sections.forEach(function (s) {
        var match = s.dataset.vesHelpCatSection === catKey;
        if (match) {
          s.removeAttribute('hidden');
          // restore any items hidden by a previous search
          $$('.ves-help-faq-item', s).forEach(function (item) {
            item.removeAttribute('hidden');
          });
        } else {
          s.setAttribute('hidden', '');
        }
      });

      try { history.replaceState({ cat: catKey }, '', '#' + catKey); } catch (e) { /* sandbox */ }
      currentView = 'category';
    }

    function showHome() {
      setHomeBlocks(true);
      setActiveCategory(activeCat);
      // close any open accordions
      sections.forEach(function (s) {
        $$('.ves-help-faq-item', s).forEach(function (item) {
          item.removeAttribute('open');
        });
      });
      $$('.ves-help-most-asked .ves-help-faq-item').forEach(function (item) {
        item.removeAttribute('open');
      });
      if (searchInfo) searchInfo.setAttribute('hidden', '');
      if (noResults) noResults.setAttribute('hidden', '');
      currentView = 'home';
    }

    function showSearch(query) {
      setHomeBlocks(false);
      var totalMatches = 0;

      sections.forEach(function (section) {
        var sectionMatches = 0;
        $$('.ves-help-faq-item', section).forEach(function (item) {
          var text = item.textContent.toLowerCase();
          if (text.indexOf(query) !== -1) {
            item.removeAttribute('hidden');
            item.setAttribute('open', '');
            sectionMatches++;
            totalMatches++;
          } else {
            item.setAttribute('hidden', '');
            item.removeAttribute('open');
          }
        });
        if (sectionMatches === 0) section.setAttribute('hidden', '');
        else section.removeAttribute('hidden');
      });

      if (searchInfo) {
        if (totalMatches > 0) {
          searchInfo.removeAttribute('hidden');
          var safe = query.replace(/[<>&"]/g, function (c) {
            return { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c];
          });
          searchInfo.innerHTML =
            '<strong>' + totalMatches + '</strong> ' +
            (totalMatches === 1 ? 'result' : 'results') +
            ' for "<strong>' + safe + '</strong>"';
        } else {
          searchInfo.setAttribute('hidden', '');
        }
      }

      if (noResults) {
        if (totalMatches === 0) noResults.removeAttribute('hidden');
        else noResults.setAttribute('hidden', '');
      }

      currentView = 'search';
    }

    // -- Mobile: the expanded panel docks directly below the clicked card
    //    (full-width inside the grid). On desktop panels stay in their
    //    original spot below the grid. Original positions are remembered so
    //    a desktop resize after mobile use restores the layout.
    var grid = $('.ves-help-categories__grid');
    sections.forEach(function (s) {
      if (!s.__vesHome) s.__vesHome = { parent: s.parentNode, next: s.nextSibling };
    });

    function isMobile() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    function placeSection(catKey, card) {
      var section = sections.filter(function (s) {
        return s.dataset.vesHelpCatSection === catKey;
      })[0];
      if (!section) return;
      if (isMobile() && grid && card) {
        grid.insertBefore(section, card.nextElementSibling);
      } else if (section.__vesHome && section.parentNode !== section.__vesHome.parent) {
        section.__vesHome.parent.insertBefore(section, section.__vesHome.next);
      }
    }

    // -- Wire card clicks
    cards.forEach(function (card) {
      if (card.dataset.vesHelpCatBound === '1') return;
      card.dataset.vesHelpCatBound = '1';
      card.addEventListener('click', function () {
        var key = card.dataset.vesHelpCat;
        // Tapping the open category on mobile collapses it back to the
        // grid. A dedicated path, NOT showHome(): showHome re-activates
        // the default category and would immediately un-hide the very
        // panel being collapsed.
        if (isMobile() && card.classList.contains('is-active') && currentView === 'category') {
          var openSection = sections.filter(function (s) {
            return s.dataset.vesHelpCatSection === key;
          })[0];
          if (openSection) {
            openSection.setAttribute('hidden', '');
            if (openSection.__vesHome && openSection.parentNode !== openSection.__vesHome.parent) {
              openSection.__vesHome.parent.insertBefore(openSection, openSection.__vesHome.next);
            }
          }
          card.classList.remove('is-active');
          card.setAttribute('aria-selected', 'false');
          currentView = 'home';
          try { history.replaceState({}, '', location.pathname); } catch (e) { /* sandbox */ }
          return;
        }
        placeSection(key, card);
        setActiveCategory(key);
      });
    });

    // -- Wire search input
    if (searchInput && searchInput.dataset.vesHelpSearchBound !== '1') {
      searchInput.dataset.vesHelpSearchBound = '1';
      searchInput.addEventListener('input', function () {
        var q = searchInput.value.trim().toLowerCase();
        if (q) {
          if (clearBtn) clearBtn.removeAttribute('hidden');
          showSearch(q);
        } else {
          if (clearBtn) clearBtn.setAttribute('hidden', '');
          showHome();
        }
      });
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          searchInput.value = '';
          if (clearBtn) clearBtn.setAttribute('hidden', '');
          showHome();
          searchInput.blur();
        }
      });
    }
    if (clearBtn && clearBtn.dataset.vesHelpSearchClearBound !== '1') {
      clearBtn.dataset.vesHelpSearchClearBound = '1';
      clearBtn.addEventListener('click', function () {
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
        }
        clearBtn.setAttribute('hidden', '');
        showHome();
      });
    }

    // -- Hash routing. Two paths:
    //    • Initial load (refresh / direct visit with hash in URL) →
    //      apply the active category silently, do NOT scroll. We want
    //      the page to start at the top so the user sees the hero,
    //      featured guide, etc. before the category section.
    //    • User-triggered hashchange (back/forward, link click) →
    //      apply the category AND scroll to the cards so the change is
    //      visible.
    function applyHashCategory(shouldScroll) {
      var hash = (location.hash || '').slice(1).split('/')[0];
      if (validKeys.indexOf(hash) === -1) return;
      setActiveCategory(hash);
      if (shouldScroll) {
        var grid = $('.ves-help-categories__grid');
        if (grid) {
          var top = grid.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      }
    }
    if (!window.__vesHelpHashBound) {
      window.__vesHelpHashBound = true;
      window.addEventListener('hashchange', function () { applyHashCategory(true); });
    }
    applyHashCategory(false);

    // Mobile first load: the initially-active category (Get started, or the
    // hash target) opens docked under its card, matching the tap behavior,
    // instead of sitting unseen below the grid.
    if (isMobile()) {
      var initialCard = cards.filter(function (c) {
        return c.classList.contains('is-active');
      })[0];
      if (initialCard) {
        placeSection(initialCard.dataset.vesHelpCat, initialCard);
        currentView = 'category';
      }
    }
  }

  // ------------------------------------------------------------------
  // 4. VIDEO LIGHTBOX
  // ------------------------------------------------------------------

  function ensureLightbox() {
    var existing = document.getElementById('ves-help-video-lightbox');
    if (existing) return existing;

    var dlg = document.createElement('dialog');
    dlg.id = 'ves-help-video-lightbox';
    dlg.className = 'ves-help-video-lightbox';
    dlg.innerHTML =
      '<div class="ves-help-video-lightbox__frame-wrapper">' +
        '<button type="button" class="ves-help-video-lightbox__close" aria-label="Close video">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
            '<line x1="6" y1="6" x2="18" y2="18"/>' +
            '<line x1="18" y1="6" x2="6" y2="18"/>' +
          '</svg>' +
        '</button>' +
        '<iframe class="ves-help-video-lightbox__frame" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>' +
      '</div>';
    document.body.appendChild(dlg);

    var frame = dlg.querySelector('.ves-help-video-lightbox__frame');
    var closeBtn = dlg.querySelector('.ves-help-video-lightbox__close');

    function close() {
      frame.src = '';
      if (dlg.open) dlg.close();
    }
    closeBtn.addEventListener('click', close);
    dlg.addEventListener('click', function (e) {
      // close when clicking the backdrop (outside the inner frame wrapper)
      var rect = dlg.querySelector('.ves-help-video-lightbox__frame-wrapper').getBoundingClientRect();
      if (
        e.clientX < rect.left || e.clientX > rect.right ||
        e.clientY < rect.top || e.clientY > rect.bottom
      ) {
        close();
      }
    });
    dlg.addEventListener('close', function () { frame.src = ''; });

    return dlg;
  }

  function openVideo(youtubeId) {
    if (!youtubeId) return;
    var dlg = ensureLightbox();
    var frame = dlg.querySelector('.ves-help-video-lightbox__frame');
    frame.src = 'https://www.youtube.com/embed/' + encodeURIComponent(youtubeId) + '?autoplay=1&rel=0';
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  }

  function bindVideoButtons() {
    // All video triggers (FAQ "Watch" buttons + the featured-guide
    // poster) open the modal lightbox. We tried inline play with
    // controls=0 but YouTube ended up hiding the playback controls
    // entirely while leaving the YouTube logo + title bar in place,
    // which was the opposite of the desired feel. The modal experience
    // is consistent and already-styled, so the poster routes there too.
    $$('[data-ves-help-video]').forEach(function (btn) {
      if (btn.dataset.vesHelpVideoBound === '1') return;
      btn.dataset.vesHelpVideoBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openVideo(btn.getAttribute('data-ves-help-video'));
      });
    });
  }

  // ------------------------------------------------------------------
  // BOOTSTRAP
  // ------------------------------------------------------------------

  function boot() {
    initHelpCenter();
    bindVideoButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind after Shopify section re-render (theme editor)
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
